import { t } from '@lingui/core/macro'
import * as sphere from 'ol/sphere'
import { formatNumber } from './format'

export function areaToString(area, units, options) {
  if (area === undefined || area === null) return '-'

  options = options || {}
  const showUnit = options.onlyValue === undefined || !options.onlyValue

  if (units !== 'imperial') {
    if (options.alwaysFieldUnits || area >= 1000) {
      return (
        formatNumber(area / 10000, { decimals: 2 }) + (showUnit ? ' ha' : '')
      )
    } else if (area < 0.1) {
      return (
        formatNumber(area * 10000, { decimals: 0 }) + (showUnit ? ' cm²' : '')
      )
    } else {
      return (
        formatNumber(area, { decimals: area < 10 ? 2 : area < 100 ? 1 : 0 }) +
        (showUnit ? ' m²' : '')
      )
    }
  } else {
    if (options.alwaysFieldUnits || area >= 404.6856) {
      // 0.1 acres
      return (
        formatNumber(area * 0.000247105, { decimals: 2 }) +
        (showUnit ? ' ac' : '')
      )
    } else if (area < 1.93548) {
      return (
        formatNumber(area * 1550.0031, { decimals: 0 }) +
        (showUnit ? ' in²' : '')
      )
    } else {
      return (
        formatNumber(area * 10.7639, { decimals: 0 }) + (showUnit ? ' ft²' : '')
      )
    }
  }
}

export function distanceToString(length, units, options) {
  if (length === undefined || length === null) return '-'
  options = options || {}
  const decimals =
    options.decimals == null || typeof options.decimals !== 'number'
      ? 2
      : options.decimals
  const absLength = Math.abs(length)

  if (units !== 'imperial') {
    if (absLength > 1000) {
      return (
        formatNumber(length / 1000, { decimals }) +
        (options.onlyValue ? '' : ' km')
      )
    } else if (absLength < 1) {
      return (
        formatNumber(length * 100, { decimals: Math.max(0, decimals - 2) }) +
        (options.onlyValue ? '' : ' cm')
      )
    } else {
      return (
        formatNumber(length, { decimals }) + (options.onlyValue ? '' : ' m')
      )
    }
  } else {
    if (absLength > 1609) {
      return (
        formatNumber(length / 1609.34, { decimals }) +
        (options.onlyValue ? '' : ' mi')
      )
    } else {
      return (
        formatNumber(length / 0.3048, { decimals }) +
        (options.onlyValue ? '' : ' ft')
      )
    }
  }
}

export function resolutionToString(r, units) {
  if (r === undefined || r === null) return '-'

  const resolutionInUnits = units !== 'imperial' ? r : r * 0.393701
  return `${formatNumber(resolutionInUnits, {
    decimals: getNumberDecimals(resolutionInUnits),
  })} ${units !== 'imperial' ? 'cm' : 'in'}/px`
}

export function massToString(m, units, options) {
  if (m === undefined || m === null) return '-'

  options = options || {}
  const showUnit = options.onlyValue === undefined || !options.onlyValue
  return units !== 'imperial'
    ? formatNumber(m, { decimals: 1 }) + (showUnit ? ' ' + 'kg' : '')
    : formatNumber(m * 2.20462, { decimals: 1 }) + (showUnit ? ' lb' : '')
}

export function prescriptionToString(p, units, options) {
  if (p === undefined || p === null) return '-'

  options = options || {}
  const showUnit = options.onlyValue === undefined || !options.onlyValue
  return units !== 'imperial'
    ? formatNumber(p, { decimals: 1 }) + (showUnit ? ' kg/ha' : '')
    : formatNumber(p * 0.892179, { decimals: 1 }) + (showUnit ? ' lb/ac' : '')
}

export function density(x, area, units) {
  return units !== 'imperial'
    ? x / (area / 10000) // units / ha
    : x / (area * 0.000247105) // units / ac
}

export function densityToString(x, area, units, name) {
  const d = density(x, area, units)
  return (
    formatNumber(d, { decimals: 0 }) +
    ' ' +
    name +
    '/' +
    (units !== 'imperial' ? 'ha' : 'acre')
  )
}

export function areaInMeters(geom) {
  return Math.abs(sphere.getArea(geom, { projection: 'EPSG:4326' }))
}

export function geometryAreaToString(geom, units) {
  return areaToString(areaInMeters(geom), units)
}

export function bytesSizeToString(bytes) {
  if (isNaN(bytes)) return t`Unknown`

  const sizes = [
    [1024 * 1024 * 1024, 'GB', 1],
    [1024 * 1024, 'MB', 1],
    [1024, 'KB', 1],
    [1, 'bytes', 0],
  ]

  bytes = Number(bytes)

  let i
  for (i = 0; i < sizes.length - 1 && bytes < sizes[i][0]; i++);
  return `${formatNumber(bytes / sizes[i][0], { decimals: sizes[i][2] })} ${
    sizes[i][1]
  }`
}

export function toHumanSize(bytes) {
  if (isNaN(bytes)) return t`Unknown`

  const sizes = [
    [1024 * 1024 * 1024, 'GB', 1],
    [1024 * 1024, 'MB', 1],
    [1024, 'KB', 1],
    [1, 'bytes', 0],
  ]

  bytes = Number(bytes)

  let i
  for (i = 0; i < sizes.length - 1 && bytes < sizes[i][0]; i++);
  return `${formatNumber(bytes / sizes[i][0], { decimals: sizes[i][2] })} ${
    sizes[i][1]
  }`
}

export function gramsToTon(grams) {
  if (grams === undefined || grams === null) return '-'
  return `${formatNumber(grams / (1000 * 1000), { decimals: 1 })} tons`
}

/**
 * Given a delta x where x is the length of a range (min,max), returns a number of decimals that is
 * suitable for display of values within this range. For small ranges, a larger number of decimals is
 * chosen, for large ranges fewer decimals are used.
 *
 * @param {number} x the length of the range
 * @param {number} fudge a fudge factor where a higher value will skew the return value towards more decimals (default is 1.2)
 */
export function getNumberDecimals(x, fudge = 1.2) {
  return Math.ceil(Math.max(0, -Math.log10(Math.abs(x)) + fudge))
}
